import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import FormComponent from '../FormComponent';

const BLUR_EVENT = 'blur';

const IS_INVALID_CLASS = 'is-invalid';
const IS_FOCUSED_CLASS = 'is-focused';

class DateComponent extends FormComponent {
	constructor(element, config) {
		super(element, config);
		this._onComplete = null;
	}
	onComplete(callback) {
		this._onComplete = callback;
	}
	init(questionData) {
		this.renderInputField(questionData);
	}
	markup(value, data, titleMarkup) {
		const inputID = `lta-date-component-id-${data.id}`;
		let dateArray = data?.value?.split('/');
		let dayValue, monthValue, yearValue;
		if (dateArray?.length > 0) {
			dayValue = dateArray[0];
			monthValue = dateArray[1];
			yearValue = dateArray[2];
		}
		const labelElement = `<label for=${inputID} class="lta-text-input__label u-text-grey-lta">${data.label}</label>`;
		return `<div class="lta-date-component u-mb-5">
					<div class="lta-date-component-title">${titleMarkup}</div>
					<div class="lta-date-component__wrapper lta-text-dob">
						${labelElement}
						${data.instructionText ? `<p class="u-body2 u-mb-1 u-text-grey-dark-slate">${data.instructionText}</p>` : ''}
						<div class="lta-text-input--error"></div>
						<div class="lta-date-component__text-wrapper u-hstack u-mb-3">
							<input type="number" inputmode="numeric" class="lta-text-input u-text-center u-mr-1 is-day" data-combined="dob" id="TextInputDay" placeholder="DD" value="${dayValue}" aria-label="Day">
							<input type="number" inputmode="numeric" class="lta-text-input u-text-center u-mr-1 is-month" data-combined="dob" id="TextInputMonth" placeholder="MM" value="${monthValue}" aria-label="Month">
							<input type="number" inputmode="numeric" class="lta-text-input u-text-center is-year" data-combined="dob" id="TextInputYear" placeholder="YYYY" value="${yearValue}" aria-label="Year">
						</div>
					</div>
				</div>`;
	}
	afterRender() {
		this.componentElement = SelectorEngine.findOne('.lta-date-component', this._element);
		this.inputFieldContainerElement = SelectorEngine.findOne('.lta-date-component__wrapper', this._element);
		this.inputFieldElements = SelectorEngine.find('input', this.inputFieldContainerElement);

		this.dayInput = SelectorEngine.findOne('.is-day', this.inputFieldContainerElement);
		this.monthInput = SelectorEngine.findOne('.is-month', this.inputFieldContainerElement);
		this.yearInput = SelectorEngine.findOne('.is-year', this.inputFieldContainerElement);

		this.dayInput.maxLength = 2;
		this.monthInput.maxLength = 2;
		this.yearInput.maxLength = 4;

		// ---- Numeric cleanup + auto-tab ----
		const cleanNumeric = (el, max) => {
			el.value = el.value.replace(/\D/g, '').slice(0, max);
		};

		// DAY → MONTH auto move
		EventHandler.on(this.dayInput, 'input', () => {
			cleanNumeric(this.dayInput, 2);
			if (this.dayInput.value.length === 2) {
				this.monthInput.focus();
			}
		});

		// MONTH → YEAR auto move
		EventHandler.on(this.monthInput, 'input', () => {
			cleanNumeric(this.monthInput, 2);
			if (this.monthInput.value.length === 2) {
				this.yearInput.focus();
			}
		});

		// YEAR max 4 characters
		EventHandler.on(this.yearInput, 'input', () => {
			cleanNumeric(this.yearInput, 4);
		});

		// ---- Backspace auto move backwards ----
		EventHandler.on(this.monthInput, 'keydown', (e) => {
			if (e.key === 'Backspace' && this.monthInput.value.length === 0) {
				this.dayInput.focus();
			}
		});
		EventHandler.on(this.yearInput, 'keydown', (e) => {
			if (e.key === 'Backspace' && this.yearInput.value.length === 0) {
				this.monthInput.focus();
			}
		});

		if (this.data?.value) {
			setTimeout(() => {
				this.setupValueObject();
			}, 100);
		}
		for (const inputField of this.inputFieldElements) {
			EventHandler.on(inputField, BLUR_EVENT, (e) => {
				// Pad day/month if needed
				if (inputField.classList.contains('is-day') || inputField.classList.contains('is-month')) {
					this.padInputValue(inputField);
				}

				// If focus is still inside the DOB component, do nothing
				setTimeout(() => {
					const activeElement = document.activeElement;
					const isStillInside = this.inputFieldContainerElement.contains(activeElement);

					if (!isStillInside) {
						this.setupValueObject();

						if (this._onComplete) {
							this._onComplete({
								valid: this.validate(),
								age: this.getAge()
							});
						}
					}
				}, 0);
			});
		}
	}
	padInputValue(inputField) {
		const value = inputField.value.trim();
		if (value && value.length === 1 && /^[0-9]$/.test(value)) {
			inputField.value = `0${value}`;
		}
	}
	setupValueObject() {
		if (this.validate()) {
			this.value = {
				...this.data,
				value: `${this.dateObject?.day}/${this.dateObject?.month}/${this.dateObject?.year}`
			};
			if (this.value) {
				this.dependentQuestion = this.value.dependentQuestion;
			}
		}
	}
	getAge() {
		if (!this.dateObject?.year) return null;

		const today = new Date();
		const birthDate = new Date(`${this.dateObject.month}/${this.dateObject.day}/${this.dateObject.year}`);

		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();

		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}

		return age;
	}
	validate() {
		let isValidValue = true;
		this.dateObject = { day: '', month: '', year: '' };
		for (const inputField of this.inputFieldElements) {
			if (!inputField.value) {
				isValidValue = false;
				break;
			} else {
				if (inputField.classList.contains('is-day')) {
					this.dateObject.day = inputField.value;
				}
				if (inputField.classList.contains('is-month')) {
					this.dateObject.month = inputField.value;
				}
				if (inputField.classList.contains('is-year')) {
					this.dateObject.year = inputField.value;
				}
			}
		}
		if (isValidValue) {
			const inputDateData = this.inputDateValidation(this.dateObject);
			if (!inputDateData.valid) {
				this.addInvalidClass(inputDateData.message);
				isValidValue = false;
			} else {
				this.removeInvalidClass();
			}
		} else {
			this.addInvalidClass(this.data?.errorMessage);
		}
		return isValidValue;
	}
	inputDateValidation(dateObject) {
		const padNumber = (num) => (Number(num) < 10 ? `0${num}` : num);

		const isCompleted = dateObject.day !== '' && dateObject.month !== '' && dateObject.year !== '';
		let valid = false;
		let message = this.data?.errorMessage;

		if (isCompleted) {
			this.dataObject = new Date(`${padNumber(dateObject.month)}/${padNumber(dateObject.day)}/${dateObject.year}`);
			const isValid = !Number.isNaN(this.dataObject.getTime());
			const isValidDate = this.dateValidate(dateObject.year, dateObject.month, dateObject.day);
			if (!isValidDate) {
				message = 'Please enter a valid date';
			} else if (isValid) {
				let currentDate = new Date();
				const isInThePast = this.dataObject <= currentDate;
				message = 'Please enter a date in the past';

				if (isInThePast) {
					let isAllowed = true;
					const { minPastYears, maxPastYears } = this.data;

					if (minPastYears?.amount) {
						const minDate = this.getPastFullDates(minPastYears.amount);
						isAllowed = this.dataObject <= minDate;
						message = minPastYears.errorMessage;
					}

					if (isAllowed && maxPastYears?.amount) {
						const maxDate = this.getPastFullDates(maxPastYears.amount);
						isAllowed = this.dataObject >= maxDate;
						message = maxPastYears.errorMessage;
					}

					valid = isAllowed;
				}
			}
		}
		return {
			message: message,
			valid: valid
		};
	}
	dateValidate(year, month, day) {
		const parseMonth = parseInt(month - 1);
		const d = new Date(year, parseMonth, day);
		if (d.getFullYear() == year && d.getMonth() == parseMonth && d.getDate() == day) {
			return true;
		}
		return false;
	}
	removeInvalidClass() {
		const errorElement = SelectorEngine.findOne('.lta-text-input--error', this.inputFieldContainerElement);
		errorElement.innerHTML = '';
		this.inputFieldContainerElement.classList.remove(IS_INVALID_CLASS);
		this.inputFieldContainerElement.classList.remove(IS_FOCUSED_CLASS);
	}

	addInvalidClass(errorMessage) {
		const errorElement = SelectorEngine.findOne('.lta-text-input--error', this.inputFieldContainerElement);
		errorElement.innerHTML = errorMessage;
		this.inputFieldContainerElement.classList.add(IS_INVALID_CLASS);
		this.inputFieldContainerElement.classList.add(IS_FOCUSED_CLASS);
	}

	getPastFullDates(n) {
		return new Date(new Date().setFullYear(new Date().getFullYear() - n));
	}
}
export default DateComponent;
